import { Controller } from '@hotwired/stimulus';
import TomSelect from 'tom-select';
import 'tom-select/dist/css/tom-select.css';

// Remote option loading (react-select's async mode), as the user types:
// - `remote-url` value: GET <url>?q= returning [{ value, label }] or the
//   typeahead [{ id, text }] shape.
// - `graphql-query` value: POST /graphql (or `remote-url` if set) with
//   { query, variables: { query: <input> } }; `graphql-path` (e.g.
//   "tags.nodes") locates the option nodes ({ id, name }) in the response.
//
// Picks re-dispatch as `select-field-component:change` ({ value, label })
// for composing controllers (search-input-component), which also use the
// reset/take/release API to keep already-picked options out of the menu.
export default class extends Controller {
  static targets = ['select'];

  static values = { remoteUrl: String, graphqlQuery: String, graphqlPath: String };

  connect() {
    // Turbo snapshots the page as-is; destroy() restores the original
    // <select> so the cached copy re-enhances cleanly on restore.
    this.handleBeforeCache = () => this.teardown();
    document.addEventListener('turbo:before-cache', this.handleBeforeCache);
    this.tomSelect = new TomSelect(this.selectTarget, this.settings());
  }

  disconnect() {
    document.removeEventListener('turbo:before-cache', this.handleBeforeCache);
    this.teardown();
  }

  // --- API for composing controllers ---

  // Clears text + selection without dispatching a change.
  reset() {
    this.tomSelect.clear(true);
    this.tomSelect.setTextboxValue('');
  }

  take(value) {
    this.tomSelect.removeOption(value);
  }

  release(value, label) {
    this.tomSelect.addOption({ value, text: label });
  }

  // --- internals ---

  settings() {
    const settings = {
      maxItems: this.selectTarget.multiple ? null : 1,
      maxOptions: null,
      allowEmptyOption: false,
      placeholder: this.selectTarget.dataset.placeholder,
      onChange: (value) => this.change(value),
    };

    if (this.selectTarget.multiple) settings.plugins = ['remove_button'];

    if (this.hasGraphqlQueryValue || this.hasRemoteUrlValue) {
      settings.shouldLoad = (query) => query.trim().length > 0;
      settings.load = this.hasGraphqlQueryValue
        ? (query, callback) => this.loadGraphqlOptions(query, callback)
        : (query, callback) => this.loadOptions(query, callback);
    }

    return settings;
  }

  change(value) {
    if (this.selectTarget.multiple) {
      this.dispatch('change', { detail: { value } });
      return;
    }

    const option = this.tomSelect.options[value];
    this.dispatch('change', {
      detail: { value: value || '', label: option ? option.text : '' },
    });
  }

  async loadOptions(query, callback) {
    try {
      const url = new URL(this.remoteUrlValue, window.location.origin);
      url.searchParams.set('q', query);
      const response = await fetch(url, { headers: { Accept: 'application/json' } });
      if (!response.ok) throw new Error(response.statusText);
      callback(this.toOptions(await response.json()));
    } catch {
      callback();
    }
  }

  async loadGraphqlOptions(query, callback) {
    try {
      const response = await fetch(this.remoteUrlValue || '/graphql', {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name=csrf-token]')?.getAttribute('content'),
        },
        body: JSON.stringify({
          query: this.graphqlQueryValue,
          variables: { query },
        }),
      });
      if (!response.ok) throw new Error(response.statusText);
      const { data, errors } = await response.json();
      if (errors || !data) throw new Error('GraphQL error');

      const nodes = this.graphqlPathValue
        .split('.')
        .reduce((node, key) => node?.[key], data);
      callback(this.toOptions(nodes || []));
    } catch {
      callback();
    }
  }

  toOptions(items) {
    return items.map((item) => ({
      value: String(item.value ?? item.id),
      text: item.label ?? item.text ?? item.name,
    }));
  }

  teardown() {
    this.tomSelect?.destroy();
    this.tomSelect = null;
  }
}
